// src/components/admin/reports/AnalyticsDashboard.jsx
import React, { useState, useEffect } from 'react';
import { getMembers, getVolunteers, fmtDate } from '../../../api/communityApi';
import { getEvents } from '../../../api/eventsApi';
import { getAllCampaigns } from '../../../api/communicationsApi';

const parseTs = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

const isThisMonth = (ts) => {
  const d = parseTs(ts);
  if (!d) return false;
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const StatCard = ({ label, value, sub }) => (
  <div className="bb-rpt-stat">
    <div className="bb-rpt-stat-val">{value ?? '—'}</div>
    <div className="bb-rpt-stat-lbl">{label}</div>
    {sub && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{sub}</div>}
  </div>
);

export default function AnalyticsDashboard({ orgId }) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const [members,    setMembers]    = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [events,     setEvents]     = useState([]);
  const [campaigns,  setCampaigns]  = useState([]);

  useEffect(() => {
    loadAll();
  }, [orgId]);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [m, v, e, c] = await Promise.all([
        getMembers(orgId),
        getVolunteers(orgId),
        getEvents(orgId),
        getAllCampaigns(orgId),
      ]);
      setMembers(m);
      setVolunteers(v);
      setEvents(e);
      setCampaigns(c);
    } catch (err) {
      setError('Failed to load analytics data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Computed stats ──────────────────────────────────────────────────────────
  const totalMembers   = members.length;
  const activeMembers  = members.filter(m => m.status === 'active').length;
  const totalVols      = volunteers.length;
  const totalHours     = volunteers.reduce((sum, v) => sum + (v.totalHours || 0), 0);
  const eventsThisMonth = events.filter(e => isThisMonth(e.start_datetime || e.startDate)).length;
  const campaignsThisMonth = campaigns.filter(c => isThisMonth(c.sentAt || c.createdAt)).length;

  // ── Engagement distribution ─────────────────────────────────────────────────
  const engDist = { high: 0, medium: 0, low: 0, at_risk: 0 };
  members.forEach(m => {
    const t = m.engagementTier || 'at_risk';
    if (engDist[t] !== undefined) engDist[t]++;
  });
  const maxEng = Math.max(...Object.values(engDist), 1);

  // ── Completed events (last 5) ───────────────────────────────────────────────
  const completedEvents = events
    .filter(e => e.status === 'COMPLETED' || e.status === 'completed')
    .sort((a, b) => {
      const da = parseTs(a.start_datetime || a.startDate);
      const db2 = parseTs(b.start_datetime || b.startDate);
      return (db2?.getTime() || 0) - (da?.getTime() || 0);
    })
    .slice(0, 5);

  // ── Top 10 volunteers by hours ──────────────────────────────────────────────
  const topVols = [...volunteers]
    .sort((a, b) => (b.totalHours || 0) - (a.totalHours || 0))
    .slice(0, 10);
  const maxHours = topVols[0]?.totalHours || 1;

  // ── Recent campaigns (last 10) ──────────────────────────────────────────────
  const recentCampaigns = campaigns.slice(0, 10);

  const CHANNEL_COLORS = { email: '#4338CA', push: '#0891B2', whatsapp: '#16A34A' };

  if (loading) {
    return (
      <div className="bb-panel">
        <div className="bb-panel-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="bb-spinner" />
          <p style={{ marginTop: 12, color: '#9CA3AF' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="bb-alert bb-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ── Overview Stats ── */}
      <div className="bb-rpt-stats">
        <StatCard label="Total Members"     value={totalMembers} />
        <StatCard label="Active Members"    value={activeMembers} />
        <StatCard label="Total Volunteers"  value={totalVols} />
        <StatCard label="Hours Volunteered" value={totalHours.toFixed(1)} />
        <StatCard label="Events This Month" value={eventsThisMonth} />
        <StatCard label="Campaigns Sent This Month" value={campaignsThisMonth} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* ── Member Engagement Chart ── */}
        <div className="bb-panel">
          <div className="bb-panel-header">
            <h4 className="bb-panel-title">Member Engagement Distribution</h4>
          </div>
          <div className="bb-panel-body">
            {[
              { tier: 'high',    label: 'High (≥70)',    colorClass: 'bb-eng-high',    dotClass: 'bb-eng-dot-high' },
              { tier: 'medium',  label: 'Medium (40–69)',colorClass: 'bb-eng-medium',  dotClass: 'bb-eng-dot-medium' },
              { tier: 'low',     label: 'Low (15–39)',   colorClass: 'bb-eng-low',     dotClass: 'bb-eng-dot-low' },
              { tier: 'at_risk', label: 'At-Risk (<15)', colorClass: 'bb-eng-at_risk', dotClass: 'bb-eng-dot-at_risk' },
            ].map(({ tier, label, dotClass }) => {
              const count = engDist[tier] || 0;
              const pct   = totalMembers > 0 ? Math.round((count / totalMembers) * 100) : 0;
              const barPct = maxEng > 0 ? Math.round((count / maxEng) * 100) : 0;
              return (
                <div key={tier} className="bb-rpt-bar-row">
                  <div className="bb-rpt-bar-label">
                    <span className={dotClass} />
                    {label}
                  </div>
                  <div className="bb-rpt-bar-track">
                    <div
                      className="bb-rpt-bar-fill"
                      style={{ width: `${barPct}%`, opacity: tier === 'at_risk' ? 1 : 0.7 + barPct * 0.003 }}
                    >
                      {barPct > 15 && <span className="bb-rpt-bar-val">{pct}%</span>}
                    </div>
                  </div>
                  <span className="bb-rpt-bar-count">{count}</span>
                </div>
              );
            })}
            <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12 }}>
              Total: {totalMembers} members
            </p>
          </div>
        </div>

        {/* ── Volunteer Hours ── */}
        <div className="bb-panel">
          <div className="bb-panel-header">
            <h4 className="bb-panel-title">Top Volunteers by Hours</h4>
          </div>
          <div className="bb-panel-body">
            {topVols.length === 0 ? (
              <p style={{ fontSize: 13, color: '#9CA3AF' }}>No volunteer data available.</p>
            ) : (
              topVols.map((v, i) => {
                const hrs  = v.totalHours || 0;
                const pct  = Math.round((hrs / maxHours) * 100);
                const name = v.displayName || `${v.firstName || ''} ${v.lastName || ''}`.trim() || 'Unknown';
                const milestones = v.earnedMilestones || [];
                return (
                  <div key={v.id} className="bb-rpt-bar-row" style={{ marginBottom: 6 }}>
                    <div className="bb-rpt-bar-label" title={name}>
                      {i + 1}. {name}
                      {milestones.length > 0 && (
                        <span style={{ marginLeft: 4, fontSize: 10 }}>
                          {milestones.includes('200hrs') ? '🦸' :
                           milestones.includes('100hrs') ? '🏆' :
                           milestones.includes('50hrs')  ? '⭐' : '🌱'}
                        </span>
                      )}
                    </div>
                    <div className="bb-rpt-bar-track">
                      <div className="bb-rpt-bar-fill" style={{ width: `${pct}%` }}>
                        {pct > 15 && <span className="bb-rpt-bar-val">{hrs}h</span>}
                      </div>
                    </div>
                    <span className="bb-rpt-bar-count">{hrs}h</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ── Event Attendance Summary ── */}
      <div className="bb-panel" style={{ marginBottom: 20 }}>
        <div className="bb-panel-header">
          <h4 className="bb-panel-title">Recent Completed Events</h4>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>Last 5 completed events</span>
        </div>
        <div className="bb-panel-body" style={{ padding: 0 }}>
          {completedEvents.length === 0 ? (
            <p style={{ padding: 20, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
              No completed events found.
            </p>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Capacity</th>
                  <th>Registered</th>
                  <th>Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {completedEvents.map(ev => {
                  const cap  = ev.capacity || ev.max_capacity || 0;
                  const reg  = ev.registrationCount || ev.registered_count || 0;
                  const rate = cap > 0 ? Math.round((reg / cap) * 100) : 0;
                  return (
                    <tr key={ev.id}>
                      <td style={{ fontWeight: 500 }}>{ev.title || ev.name || '—'}</td>
                      <td>{fmtDate(ev.start_datetime || ev.startDate)}</td>
                      <td>{cap || '—'}</td>
                      <td>{reg}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            flex: 1, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${rate}%`, height: '100%',
                              background: rate >= 80 ? '#10B981' : rate >= 50 ? '#F59E0B' : '#EF4444',
                              borderRadius: 3
                            }} />
                          </div>
                          <span style={{ fontSize: 12, color: '#374151', minWidth: 32 }}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Campaign Performance ── */}
      <div className="bb-panel">
        <div className="bb-panel-header">
          <h4 className="bb-panel-title">Campaign Performance</h4>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>Most recent 10 campaigns</span>
        </div>
        <div className="bb-panel-body" style={{ padding: 0 }}>
          {recentCampaigns.length === 0 ? (
            <p style={{ padding: 20, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
              No campaigns found.
            </p>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Recipients</th>
                  <th>Sent Date</th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.name || c.subject || '—'}</td>
                    <td>
                      <span style={{
                        background: CHANNEL_COLORS[c._channel] || '#6B7280',
                        color: '#fff', fontSize: 11, padding: '2px 8px',
                        borderRadius: 12, fontWeight: 600
                      }}>
                        {c._channelLabel || c._channel || '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        color: c.status === 'sent' ? '#065F46' : c.status === 'scheduled' ? '#92400E' : '#374151',
                        background: c.status === 'sent' ? '#D1FAE5' : c.status === 'scheduled' ? '#FEF3C7' : '#F3F4F6',
                        padding: '2px 8px', borderRadius: 10
                      }}>
                        {c.status || '—'}
                      </span>
                    </td>
                    <td>{c.recipientCount ?? c.audienceSize ?? '—'}</td>
                    <td>{fmtDate(c.sentAt || c.scheduledFor || c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
